import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Observable, forkJoin, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { MockTestSecurityService } from '../../../core/security/mock-test-security.service';
import { ExamEventTrackerService } from '../../../core/services/exam-event-tracker.service';
import { StorageService } from '../../../core/services/storage.service';
import {
  ActiveExamMeta,
  ExamResultState,
  PreparedQuestionPaperState,
  SubmitExamPayload,
} from '../model/test-series.model';
import { TestSeriesService } from './test-series.service';

const ACTIVE_EXAM_META_KEY = 'activeExamMeta';

/**
 * Start-exam answers 409 while this or another exam is live on another
 * device/platform. The student has to close that exam first, so callers
 * must not let them retry from the same screen.
 */
export function isExamInProgressElsewhere(error: unknown): boolean {
  return error instanceof HttpErrorResponse && error.status === 409;
}

@Injectable({
  providedIn: 'root',
})
export class ExamSessionService {
  private readonly storageService = inject(StorageService);
  private readonly testSeriesService = inject(TestSeriesService);
  private readonly securityService = inject(MockTestSecurityService);
  private readonly eventTracker = inject(ExamEventTrackerService);
  private readonly activeExamMetaState = signal<ActiveExamMeta | null>(
    this.storageService.getItem<ActiveExamMeta>(ACTIVE_EXAM_META_KEY),
  );
  private readonly preparedQuestionPaperStateValue = signal<PreparedQuestionPaperState | null>(null);
  private readonly pendingSubmitPayloadValue = signal<SubmitExamPayload | null>(null);
  private readonly pendingExamResultStateValue = signal<ExamResultState | null>(null);

  readonly activeExamMeta = this.activeExamMetaState.asReadonly();
  readonly preparedQuestionPaperState = this.preparedQuestionPaperStateValue.asReadonly();
  readonly pendingSubmitPayload = this.pendingSubmitPayloadValue.asReadonly();
  readonly pendingExamResultState = this.pendingExamResultStateValue.asReadonly();

  setActiveExamMeta(meta: ActiveExamMeta): void {
    this.activeExamMetaState.set(meta);
    this.storageService.setItem(ACTIVE_EXAM_META_KEY, meta);
  }

  /**
   * Entry point from the Start Test popup. A new test is only stored here —
   * the start API (which starts the exam timer on the server) is called once
   * the student leaves the instructions, so instruction-reading time isn't
   * counted. A resumed test skips the instructions, so it is started now.
   */
  openAttempt(meta: ActiveExamMeta): Observable<ActiveExamMeta> {
    this.clearPreparedQuestionPaperState();

    if (meta.isResume) {
      return this.startAttempt(meta);
    }

    this.setActiveExamMeta(meta);
    return of(meta);
  }

  /**
   * Calls the start API for the exam in `meta`, stores the meta with the
   * returned attemptId and opens the attempt's event socket. The start API
   * also resumes a paused attempt (same attemptId), and answers 409 while
   * this or another exam is live on another device/platform.
   */
  startAttempt(meta: ActiveExamMeta): Observable<ActiveExamMeta> {
    return this.testSeriesService.startExam(meta.examId).pipe(
      map((response) => ({ ...meta, attemptId: this.testSeriesService.extractAttemptId(response) })),
      tap((startedMeta) => {
        this.setActiveExamMeta(startedMeta);
        this.eventTracker.clearSessionStop();
        this.beginEventTracking();
      }),
      catchError((error: unknown) => {
        // Blocked by an exam live elsewhere: drop this exam's stored session so
        // nothing (e.g. a reload of the question paper) tries to run it here.
        if (isExamInProgressElsewhere(error) && this.activeExamMetaState()?.examId === meta.examId) {
          this.clearSession();
        }
        return throwError(() => error);
      }),
    );
  }

  /**
   * After a page reload the server has already paused the attempt (closing
   * the tab pauses it), so it is resumed through the start API before the
   * socket reconnects. Already-connected or never-started attempts just
   * (re)open tracking.
   */
  resumeAttemptAfterReload(): Observable<ActiveExamMeta | null> {
    const meta = this.activeExamMetaState();

    if (!meta?.attemptId || this.eventTracker.isConnectedTo(meta.attemptId)) {
      this.beginEventTracking();
      return of(meta);
    }

    return this.startAttempt(meta);
  }

  /**
   * Opens the event socket for the active attempt if it isn't open yet
   * (e.g. after a page reload). No-op without an attemptId.
   */
  beginEventTracking(): void {
    const meta = this.activeExamMetaState();

    if (!meta?.attemptId) {
      return;
    }

    this.eventTracker.beginSession({
      attemptId: meta.attemptId,
      studentId: this.testSeriesService.getStoredUserId(),
      testId: meta.examId,
    });
  }

  /** Reports SESSION_ENDED and closes the event socket (attempt still in progress). */
  endEventTracking(): void {
    this.eventTracker.endSession();
  }

  setPreparedQuestionPaperState(state: PreparedQuestionPaperState): void {
    this.preparedQuestionPaperStateValue.set(state);
  }

  clearPreparedQuestionPaperState(): void {
    this.preparedQuestionPaperStateValue.set(null);
  }

  setPendingSubmission(payload: SubmitExamPayload, examResultState: ExamResultState): void {
    this.pendingSubmitPayloadValue.set(payload);
    this.pendingExamResultStateValue.set(examResultState);
  }

  clearPendingSubmission(): void {
    this.pendingSubmitPayloadValue.set(null);
    this.pendingExamResultStateValue.set(null);
  }

  clearSession(): void {
    this.preparedQuestionPaperStateValue.set(null);
    this.activeExamMetaState.set(null);
    this.clearPendingSubmission();
    this.storageService.removeItem(ACTIVE_EXAM_META_KEY);
  }

  ensurePreparedQuestionPaperState(examId: string): Observable<PreparedQuestionPaperState> {
    const currentState = this.preparedQuestionPaperStateValue();

    if (currentState?.examId === examId) {
      return of(currentState);
    }

    const activeExamMeta = this.activeExamMetaState();

    if (!activeExamMeta || activeExamMeta.examId !== examId) {
      return throwError(() => new Error('Active exam session not found.'));
    }

    return forkJoin({
      questions: this.testSeriesService.getExamQuestions(examId),
      // Settings are optional for loading the paper — fall back to all-off.
      examDetails: this.testSeriesService.getExamDetails(examId).pipe(catchError(() => of(null))),
    }).pipe(
      map(({ questions, examDetails }) =>
        this.testSeriesService.prepareQuestionPaperState(
          examId,
          questions,
          activeExamMeta,
          this.testSeriesService.extractExamSettings(examDetails),
          examDetails?.testParts,
          examDetails?.resultConfig,
        ),
      ),
      tap((preparedState) => {
        this.preparedQuestionPaperStateValue.set(preparedState);
        this.securityService.setProctoringEnabled(preparedState.settings.enableProctoring);
      }),
    );
  }
}
